import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { TradePolicyMetric } from "../../../types/tradePolicy";
import type { ComparisonRun } from "./simulatorComparisonTypes";

export type SimulatorComparisonRunsContextValue = {
  comparisonRuns: ComparisonRun[];
  runsForTabs: ComparisonRun[];
  selectedRunId: string;
  setSelectedRunId: (id: string) => void;
  selectedRun: ComparisonRun | null;
  sharedBaseline: TradePolicyMetric | undefined;
  showRunTabs: boolean;
};

function isNonEmptyBaseline(value: unknown): value is TradePolicyMetric {
  return value != null && typeof value === "object" && Object.keys(value as object).length > 0;
}

/**
 * Prefer the selected run’s own baseline so filtered metrics and baseline stay paired.
 * Fall back to the first non-empty baseline only while the selected run is still loading.
 */
export function resolveComparisonBaseline(
  selectedRun: ComparisonRun | null | undefined,
  comparisonRuns: ComparisonRun[]
): TradePolicyMetric | undefined {
  const fromSelected = selectedRun?.result?.baseline;
  if (isNonEmptyBaseline(fromSelected)) return fromSelected;
  for (const r of comparisonRuns) {
    const b = r.result?.baseline;
    if (isNonEmptyBaseline(b)) return b;
  }
  return undefined;
}

const SimulatorComparisonRunsContext = createContext<SimulatorComparisonRunsContextValue | null>(null);

export function useSimulatorComparisonRuns(): SimulatorComparisonRunsContextValue {
  const ctx = useContext(SimulatorComparisonRunsContext);
  if (!ctx) {
    throw new Error("useSimulatorComparisonRuns must be used within SimulatorComparisonRunsProvider");
  }
  return ctx;
}

export function SimulatorComparisonRunsProvider(props: {
  comparisonRuns: ComparisonRun[];
  initialSelectedRunId?: string | null;
  /** When true, never show run tabs (e.g. saved-simulation detail page). */
  hideRunTabs?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const { comparisonRuns, initialSelectedRunId, hideRunTabs = false, children } = props;
  const [selectedRunId, setSelectedRunId] = useState("");
  const prevLenRef = useRef(0);

  const runsForTabs = useMemo(() => [...comparisonRuns].reverse(), [comparisonRuns]);

  useEffect(() => {
    const n = comparisonRuns.length;
    const prev = prevLenRef.current;
    if (n === 0) {
      setSelectedRunId("");
    } else if (n > prev) {
      setSelectedRunId(comparisonRuns[0]?.id ?? "");
    } else {
      setSelectedRunId((id) => {
        if (id && comparisonRuns.some((run) => run.id === id)) return id;
        return runsForTabs[runsForTabs.length - 1]?.id ?? comparisonRuns[0]?.id ?? "";
      });
    }
    prevLenRef.current = n;
  }, [comparisonRuns, runsForTabs]);

  useEffect(() => {
    if (!initialSelectedRunId) return;
    if (comparisonRuns.some((run) => run.id === initialSelectedRunId)) {
      setSelectedRunId(initialSelectedRunId);
    }
  }, [initialSelectedRunId, comparisonRuns]);

  const selectedRun =
    runsForTabs.find((run) => run.id === selectedRunId) ?? runsForTabs[runsForTabs.length - 1] ?? null;

  // Always use the selected run's own baseline. Cross-run "first non-empty" was wrong when a
  // report hydrates many linked sims: filtered came from the selected run but baseline came
  // from the newest run, so the table disagreed with the standalone simulation page.
  const sharedBaseline = useMemo(
    () => resolveComparisonBaseline(selectedRun, comparisonRuns),
    [comparisonRuns, selectedRun]
  );

  const value = useMemo(
    (): SimulatorComparisonRunsContextValue => ({
      comparisonRuns,
      runsForTabs,
      selectedRunId,
      setSelectedRunId,
      selectedRun,
      sharedBaseline,
      showRunTabs: !hideRunTabs && runsForTabs.length > 1,
    }),
    [comparisonRuns, hideRunTabs, runsForTabs, selectedRun, selectedRunId, sharedBaseline]
  );

  return (
    <SimulatorComparisonRunsContext.Provider value={value}>{children}</SimulatorComparisonRunsContext.Provider>
  );
}
